import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { MapPin, Calendar, Users, Home, Euro, User, Mail, Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08
    }
  }
};

interface TravelerSearchFormProps {
  onProgressChange?: (progress: number) => void;
}

export default function TravelerSearchForm({ onProgressChange }: TravelerSearchFormProps) {
  const { t } = useLanguage();
  const ts = t.travelerSearch;

  const [destination, setDestination] = useState("");
  const [arrivalDate, setArrivalDate] = useState<Date | undefined>();
  const [departureDate, setDepartureDate] = useState<Date | undefined>();
  const [guests, setGuests] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [budget, setBudget] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const calculateProgress = () => {
    let filled = 0;
    const total = 7; // required fields
    if (destination) filled++;
    if (arrivalDate) filled++;
    if (departureDate) filled++;
    if (guests) filled++;
    if (firstName) filled++;
    if (email) filled++;
    if (phone) filled++;
    return (filled / total) * 100;
  };

  const progress = calculateProgress();

  useEffect(() => {
    onProgressChange?.(progress);
  }, [progress, onProgressChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!destination || !arrivalDate || !departureDate || !guests || !firstName.trim() || !email.trim() || !phone.trim()) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Veuillez entrer une adresse email valide");
      setIsSubmitting(false);
      return;
    }

    if (!captchaToken) {
      toast.error("Veuillez compléter le captcha");
      setIsSubmitting(false);
      return;
    }

    const destinationLabels: Record<string, string> = {
      "aix": ts?.destinations?.aixEnProvence || "Aix-en-Provence",
      "luberon": ts?.destinations?.luberon || "Luberon",
      "alpilles": ts?.destinations?.alpilles || "Alpilles",
      "gordes": ts?.destinations?.gordes || "Gordes",
      "cote-azur": ts?.destinations?.coteDAzur || "Côte d'Azur",
      "saint-tropez": ts?.destinations?.saintTropez || "Saint-Tropez",
      "other": ts?.destinations?.other || "Autre",
    };

    const propertyTypeLabels: Record<string, string> = {
      "villa": ts?.propertyTypes?.villa || "Villa",
      "apartment": ts?.propertyTypes?.apartment || "Appartement",
      "mas": ts?.propertyTypes?.mas || "Mas / Bastide",
      "any": ts?.propertyTypes?.any || "Peu importe",
    };

    const budgetLabels: Record<string, string> = {
      "under200": ts?.budgets?.under200 || "< 200€",
      "200-500": ts?.budgets?.from200to500 || "200-500€",
      "500-1000": ts?.budgets?.from500to1000 || "500-1000€",
      "over1000": ts?.budgets?.over1000 || "> 1000€",
    };

    const destLabel = destinationLabels[destination] || destination;
    const arrival = format(arrivalDate, "dd/MM/yyyy");
    const departure = format(departureDate, "dd/MM/yyyy");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "9b248f28-7389-4c29-a486-ab80ea310230",
          cc: "esl@outlook.fr",
          subject: `[Séjour] Nouvelle demande - ${destLabel} - ${guests} voyageur(s) - ${arrival} au ${departure}`,
          from_name: firstName,
          name: firstName,
          email,
          phone,
          destination: destLabel,
          dates: `${arrival} → ${departure}`,
          nombre_voyageurs: `${guests} voyageur(s)`,
          type_de_bien: propertyTypeLabels[propertyType] || "Non précisé",
          budget_par_nuit: budgetLabels[budget] || "Non précisé",
          message: message || "Aucun message",
          "h-captcha-response": captchaToken,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success("Demande envoyée ! Nous vous répondrons sous 24h.");
        setDestination("");
        setArrivalDate(undefined);
        setDepartureDate(undefined);
        setGuests("");
        setPropertyType("");
        setBudget("");
        setFirstName("");
        setEmail("");
        setPhone("");
        setMessage("");
        setCaptchaToken(null);
        captchaRef.current?.resetCaptcha();
      } else {
        toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } catch (error) {
      console.error("Erreur Web3Forms:", error);
      toast.error("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial="initial"
      animate="animate"
      variants={staggerContainer}
      className="relative bg-card/80 backdrop-blur-sm border border-border/50 p-4 md:p-5 space-y-4"
    >
      {/* Stay Details */}
      <motion.div variants={fadeInUp} className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-[1px] bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Votre Séjour</span>
          <div className="flex-1 h-[1px] bg-border" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {/* Destination */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <MapPin className="w-3.5 h-3.5" />
              {ts?.destination || "Destination"} *
            </label>
            <Select value={destination} onValueChange={setDestination}>
              <SelectTrigger className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors">
                <SelectValue placeholder={ts?.selectDestination || "Sélectionner"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aix">{ts?.destinations?.aixEnProvence || "Aix-en-Provence"}</SelectItem>
                <SelectItem value="luberon">{ts?.destinations?.luberon || "Luberon"}</SelectItem>
                <SelectItem value="alpilles">{ts?.destinations?.alpilles || "Alpilles"}</SelectItem>
                <SelectItem value="gordes">{ts?.destinations?.gordes || "Gordes"}</SelectItem>
                <SelectItem value="cote-azur">{ts?.destinations?.coteDAzur || "Côte d'Azur"}</SelectItem>
                <SelectItem value="saint-tropez">{ts?.destinations?.saintTropez || "Saint-Tropez"}</SelectItem>
                <SelectItem value="other">{ts?.destinations?.other || "Autre"}</SelectItem>
              </SelectContent>
            </Select>
          </motion.div>

          {/* Arrival Date */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Calendar className="w-3.5 h-3.5" />
              {ts?.arrivalDate || "Arrivée"} *
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full h-9 text-sm justify-start font-normal bg-background/50 border-border/50 hover:border-foreground transition-colors">
                  {arrivalDate ? format(arrivalDate, "dd MMM yyyy", { locale: frLocale }) : (ts?.selectDate || "Sélectionner")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={arrivalDate}
                  onSelect={setArrivalDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </motion.div>

          {/* Departure Date */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Calendar className="w-3.5 h-3.5" />
              {ts?.departureDate || "Départ"} *
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full h-9 text-sm justify-start font-normal bg-background/50 border-border/50 hover:border-foreground transition-colors">
                  {departureDate ? format(departureDate, "dd MMM yyyy", { locale: frLocale }) : (ts?.selectDate || "Sélectionner")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={departureDate}
                  onSelect={setDepartureDate}
                  disabled={(date) => date < (arrivalDate || new Date())}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </motion.div>

          {/* Guests */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Users className="w-3.5 h-3.5" />
              {ts?.guests || "Voyageurs"} *
            </label>
            <Select value={guests} onValueChange={setGuests}>
              <SelectTrigger className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors">
                <SelectValue placeholder={ts?.selectGuests || "Nombre"} />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((num) => (
                  <SelectItem key={num} value={num.toString()}>
                    {num} {num === 1 ? "voyageur" : "voyageurs"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </motion.div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-2">
          {/* Property Type */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Home className="w-3.5 h-3.5" />
              {ts?.propertyType || "Type de bien"}
            </label>
            <Select value={propertyType} onValueChange={setPropertyType}>
              <SelectTrigger className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors">
                <SelectValue placeholder={ts?.selectPropertyType || "Peu importe"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="villa">{ts?.propertyTypes?.villa || "Villa"}</SelectItem>
                <SelectItem value="apartment">{ts?.propertyTypes?.apartment || "Appartement"}</SelectItem>
                <SelectItem value="mas">{ts?.propertyTypes?.mas || "Mas / Bastide"}</SelectItem>
                <SelectItem value="any">{ts?.propertyTypes?.any || "Peu importe"}</SelectItem>
              </SelectContent>
            </Select>
          </motion.div>

          {/* Budget */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Euro className="w-3.5 h-3.5" />
              {ts?.budget || "Budget / nuit"}
            </label>
            <Select value={budget} onValueChange={setBudget}>
              <SelectTrigger className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors">
                <SelectValue placeholder={ts?.selectBudget || "Optionnel"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="under200">{ts?.budgets?.under200 || "< 200€"}</SelectItem>
                <SelectItem value="200-500">{ts?.budgets?.from200to500 || "200-500€"}</SelectItem>
                <SelectItem value="500-1000">{ts?.budgets?.from500to1000 || "500-1000€"}</SelectItem>
                <SelectItem value="over1000">{ts?.budgets?.over1000 || "> 1000€"}</SelectItem>
              </SelectContent>
            </Select>
          </motion.div>
        </div>
      </motion.div>

      {/* Contact Section */}
      <motion.div variants={fadeInUp} className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-[1px] bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Vos Coordonnées</span>
          <div className="flex-1 h-[1px] bg-border" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <User className="w-3.5 h-3.5" />
              {ts?.firstName || "Prénom"} *
            </label>
            <Input
              type="text"
              placeholder={ts?.firstNamePlaceholder || "Votre prénom"}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onFocus={() => setFocusedField("firstName")}
              onBlur={() => setFocusedField(null)}
              maxLength={100}
              className={`h-9 text-sm bg-background/50 border-border/50 transition-all duration-300 ${focusedField === "firstName" ? "border-foreground shadow-sm" : ""}`}
            />
          </motion.div>

          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Mail className="w-3.5 h-3.5" />
              {ts?.email || "Email"} *
            </label>
            <Input
              type="email"
              placeholder={ts?.emailPlaceholder || "votre@email.fr"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocusedField("email")}
              onBlur={() => setFocusedField(null)}
              maxLength={255}
              className={`h-9 text-sm bg-background/50 border-border/50 transition-all duration-300 ${focusedField === "email" ? "border-foreground shadow-sm" : ""}`}
            />
          </motion.div>

          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Phone className="w-3.5 h-3.5" />
              {ts?.phone || "Téléphone"} *
            </label>
            <Input
              type="tel"
              placeholder={ts?.phonePlaceholder || "+33 6 00 00 00 00"}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onFocus={() => setFocusedField("phone")}
              onBlur={() => setFocusedField(null)}
              maxLength={20}
              className={`h-9 text-sm bg-background/50 border-border/50 transition-all duration-300 ${focusedField === "phone" ? "border-foreground shadow-sm" : ""}`}
            />
          </motion.div>
        </div>

        {/* Message */}
        <motion.div variants={fadeInUp} className="space-y-1.5">
          <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
            <MessageSquare className="w-3.5 h-3.5" />
            {ts?.message || "Préférences / Message"}
          </label>
          <Textarea
            placeholder={ts?.messagePlaceholder || "Ex: vue mer, piscine, proche centre-ville..."}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={1000}
            className="text-sm bg-background/50 border-border/50 min-h-[60px] focus:border-foreground transition-colors"
          />
        </motion.div>
      </motion.div>

      {/* hCaptcha + Submit */}
      <motion.div variants={fadeInUp} className="space-y-3">
        <div className="flex justify-center transform scale-[0.85] origin-center -my-2">
          <HCaptcha
            ref={captchaRef}
            sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
            reCaptchaCompat={false}
            size="normal"
            theme="dark"
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken(null)}
          />
        </div>
        <Button
          type="submit"
          disabled={isSubmitting || !captchaToken}
          className="w-full bg-gold text-black hover:bg-gold/90 transition-all duration-300 uppercase tracking-wider text-xs"
        >
          {isSubmitting ? (ts?.submitting || "Envoi en cours...") : (ts?.submit || "Envoyer ma demande")}
        </Button>
      </motion.div>
    </motion.form>
  );
}
